import type { LeadStageKey } from './types';

/** Hebrew labels for the stage filter dropdown. */
export const STAGE_FILTERS: { key: LeadStageKey; label: string }[] = [
  { key: 'no_answer', label: 'לא ענה / חדש' },
  { key: 'in_progress', label: 'בתהליך' },
  { key: 'quoted', label: 'התקבלה הצעת מחיר' },
  { key: 'interested', label: 'מעוניין להתקדם' },
  { key: 'not_interested', label: 'לא מעוניין / אבוד' },
  { key: 'opt_out', label: 'הוסר מרשימת תפוצה' },
  { key: 'manual_review', label: 'דורש טיפול ידני' },
];

/**
 * Hebrew labels for the machine-oriented lead.status ("סטטוס מערכת").
 * Never show the raw English key (manual_review / collecting …) in the CRM.
 */
export const SYSTEM_STATUS_LABELS: Record<string, string> = {
  collecting: 'באיסוף פרטים',
  quoted: 'נשלחה הצעת מחיר',
  manual_review: 'דורש טיפול ידני',
  follow_up: 'למעקב',
  not_insurable: 'לא ניתן לבטח',
  finalizing: 'משלים פרטים סופיים',
  finalized: 'הושלם',
  callback_requested: 'ביקש חזרה טלפונית',
};

/** Resolve a machine status key to its Hebrew CRM label (falls back to the raw key). */
export function systemStatusLabel(status?: string): string {
  if (!status) return '—';
  return SYSTEM_STATUS_LABELS[status] || status;
}

export const COVERAGE_LABELS: Record<string, string> = {
  comprehensive: 'חובה + מקיף',
  third_party: "חובה + צד ג'",
};

export const DRIVERS_LABELS: Record<string, string> = {
  single: 'נהג יחיד',
  two: 'שני נהגים',
  any: 'כל נהג',
};

export const GENDER_LABELS: Record<string, string> = {
  male: 'זכר',
  female: 'נקבה',
};

/**
 * Human-readable text for the value the pricing engine will use when a field is
 * left empty (mirrors the fallbacks in src/services/quallaApi.ts buildBidPayload).
 * Shown as a "ברירת מחדל" badge in the lead detail screen so an agent knows what
 * will be sent to the insurers if they don't fill the field in.
 */
export const LEAD_DEFAULTS: Record<string, string> = {
  youngest_driver_age: '30',
  youngest_driver_birth_date: 'מחושב מהגיל',
  youngest_driver_gender: 'זכר',
  youngest_driver_license_years: '3',
  drivers_count: 'נהג יחיד',
  coverage_type: 'חובה + מקיף',
  insurance_start_date: 'היום',
  main_driver_zip: 'תל אביב–יפו (6765491)',
  prior_coverage_1: 'מקיף',
  prior_coverage_2: 'מקיף',
  prior_coverage_3: 'מקיף',
  is_married: 'לא נשוי/אה',
  drives_on_saturday: 'כן',
};

/**
 * Display an Israeli phone number without the 972 country code (e.g.
 * "972501234567" -> "0501234567"). Non-972 numbers are returned as-is.
 */
export function formatPhone(phone?: string): string {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('972')) return '0' + digits.slice(3);
  return phone;
}

/** ILS/currency-aware price formatting. */
export function formatPrice(price?: number, currency = 'ILS'): string {
  if (price === undefined || price === null) return '—';
  const symbol = currency === 'ILS' ? '₪' : currency;
  return `${price.toLocaleString()} ${symbol}`;
}

/** Short, locale-friendly date-time for the "last updated" column. */
export function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
